import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import { useNotification } from '../../components/ui/NotificationProvider';

const translateAdminError = (message, fallback = 'Ocurrió un error. Intenta nuevamente.') => {
  const raw = String(message || '').trim();

  if (!raw) return fallback;

  const text = raw.toLowerCase();

  if (
    text.includes('already been registered') ||
    text.includes('user already registered') ||
    text.includes('already registered') ||
    text.includes('already exists')
  ) {
    return 'Este correo ya está registrado en Mercado Bulk. Puedes administrarlo desde la lista de usuarios.';
  }

  if (
    text.includes('invalid login credentials') ||
    text.includes('invalid credentials')
  ) {
    return 'El correo o la contraseña son incorrectos.';
  }

  if (
    text.includes('email rate limit') ||
    text.includes('rate limit exceeded') ||
    text.includes('too many requests')
  ) {
    return 'Se han realizado demasiados intentos. Espera unos minutos e inténtalo nuevamente.';
  }

  if (
    text.includes('user not found') ||
    text.includes('user does not exist')
  ) {
    return 'El usuario no existe.';
  }

  if (
    text.includes('email not confirmed') ||
    text.includes('email_not_confirmed')
  ) {
    return 'El correo electrónico todavía no ha sido confirmado.';
  }

  if (
    text.includes('signup is disabled') ||
    text.includes('signups not allowed') ||
    text.includes('signup disabled')
  ) {
    return 'El registro público de usuarios está deshabilitado.';
  }

  if (
    text.includes('password should be at least') ||
    text.includes('password must be at least')
  ) {
    return 'La contraseña no cumple con la longitud mínima requerida.';
  }

  if (
    text.includes('new password should be different')
  ) {
    return 'La nueva contraseña debe ser diferente de la contraseña anterior.';
  }

  if (
    text.includes('database error') ||
    text.includes('error saving new user') ||
    text.includes('error creating user')
  ) {
    return 'No fue posible guardar el usuario. Intenta nuevamente.';
  }

  if (
    text.includes('jwt expired') ||
    text.includes('invalid jwt') ||
    text.includes('token has expired')
  ) {
    return 'Tu sesión expiró. Inicia sesión nuevamente.';
  }

  if (
    text.includes('failed to fetch') ||
    text.includes('network request failed') ||
    text.includes('network error')
  ) {
    return 'No fue posible conectarse con el servidor. Intenta nuevamente.';
  }

  if (
    text.includes('not authorized') ||
    text.includes('unauthorized')
  ) {
    return 'No tienes autorización para realizar esta acción.';
  }

  if (
    text.includes('forbidden')
  ) {
    return 'No tienes permisos para realizar esta acción.';
  }

  return fallback;
};

const EMPTY_FORM = {
  full_name: '',
  email: '',
  business_name: '',
  phone: '',
  market: 'MCO'
};

const AdminUsers = () => {
  const navigate = useNavigate();
  const {
    showSuccess,
    showError,
    showWarning,
    showConfirmation
  } = useNotification();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [actionUserId, setActionUserId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const adminFetch = useCallback(async (url, options = {}) => {
    const {
      data: { session }
    } = await supabase.auth.getSession();

    if (!session?.access_token) {
      const err = new Error('Tu sesión expiró. Inicia sesión nuevamente.');
      err.status = 401;
      throw err;
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
        ...(options.headers || {})
      }
    });

    const data = await response.json().catch(() => ({
      success: false,
      message: 'Respuesta no válida del servidor'
    }));

    if (!response.ok) {
      const err = new Error(
        translateAdminError(
          data?.message,
          `No fue posible completar la operación. Código ${response.status}.`
        )
      );

      err.status = response.status;
      throw err;
    }

    return data;
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      // Comprobar primero que realmente sea administrador.
      await adminFetch('/api/admin/me');

      const result =
        await adminFetch('/api/admin/users');

      setUsers(
        Array.isArray(result?.data)
          ? result.data
          : []
      );
    } catch (err) {
      if (err?.status === 401) {
        navigate('/login', { replace: true });
        return;
      }

      if (err?.status === 403) {
        navigate('/dashboard', { replace: true });
        return;
      }

      setError(
        err?.message ||
        'No fue posible cargar los usuarios.'
      );
    } finally {
      setLoading(false);
    }
  }, [adminFetch, navigate]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const updateField = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleInvite = async (event) => {
    event.preventDefault();

    setError('');
    setSuccess('');

    if (!form.full_name.trim()) {
      setError('El nombre es obligatorio.');
      return;
    }

    if (!form.email.trim()) {
      setError('El correo es obligatorio.');
      return;
    }

    setInviting(true);

    try {
      const result =
        await adminFetch(
          '/api/admin/users/invite',
          {
            method: 'POST',
            body: JSON.stringify({
              full_name:
                form.full_name.trim(),

              email:
                form.email.trim().toLowerCase(),

              business_name:
                form.business_name.trim(),

              phone:
                form.phone.trim(),

              market:
                form.market.trim() || 'MCO'
            })
          }
        );

      const inviteMessage =
        `Invitación enviada a ${result?.data?.email || form.email}.`;

      setSuccess(inviteMessage);

      showSuccess(inviteMessage, {
        title: 'Usuario invitado'
      });

      setForm(EMPTY_FORM);
      setShowForm(false);

      await loadUsers();
    } catch (err) {
      const errorMessage =
        err?.message ||
        'No fue posible invitar al usuario.';

      setError(errorMessage);

      showError(errorMessage, {
        title: 'Error al invitar usuario'
      });
    } finally {
      setInviting(false);
    }
  };

  const executeToggleUser = async (item) => {
    if (!item?.id) return;

    setError('');
    setSuccess('');
    setActionUserId(item.id);

    try {
      const action = item.active ? 'disable' : 'enable';

      await adminFetch(
        `/api/admin/users/${item.id}/${action}`,
        {
          method: 'POST'
        }
      );

      const successMessage =
        item.active
          ? `Usuario ${item.email} desactivado correctamente.`
          : `Usuario ${item.email} reactivado correctamente.`;

      setSuccess(successMessage);

      showSuccess(successMessage, {
        title: item.active
          ? 'Usuario desactivado'
          : 'Usuario reactivado'
      });

      await loadUsers();
    } catch (err) {
      const errorMessage =
        err?.message ||
        'No fue posible cambiar el estado del usuario.';

      setError(errorMessage);

      showError(errorMessage, {
        title: 'Error al cambiar estado'
      });
    } finally {
      setActionUserId(null);
    }
  };

  const handleToggleUser = (item) => {
    if (!item?.id) return;

    if (!item.active) {
      executeToggleUser(item);
      return;
    }

    showConfirmation({
      title: 'Desactivar usuario',
      message:
        `¿Seguro que deseas desactivar la cuenta ${item.email}? El usuario no podrá acceder hasta que vuelvas a reactivarlo.`,
      confirmText: 'Desactivar',
      cancelText: 'Cancelar',
      type: 'warning',
      onConfirm: () => executeToggleUser(item)
    });
  };

  const executeResetPassword = async (item) => {
    if (!item?.email) return;

    setError('');
    setSuccess('');
    setActionUserId(item.id);

    try {
      const redirectTo =
        `${window.location.origin}/reset-password`;

      const { error } =
        await supabase.auth.resetPasswordForEmail(
          item.email,
          {
            redirectTo
          }
        );

      if (error) {
        throw error;
      }

      const successMessage =
        `Correo para restablecer contraseña enviado a ${item.email}.`;

      setSuccess(successMessage);

      showSuccess(successMessage, {
        title: 'Recuperación enviada'
      });
    } catch (err) {
      const errorMessage =
        translateAdminError(
          err?.message,
          'No fue posible enviar el correo de recuperación.'
        );

      setError(errorMessage);

      showError(errorMessage, {
        title: 'Error de recuperación'
      });
    } finally {
      setActionUserId(null);
    }
  };

  const handleResetPassword = (item) => {
    if (!item?.email) return;

    showConfirmation({
      title: 'Restablecer contraseña',
      message:
        `Se enviará un correo a ${item.email} para que pueda crear una nueva contraseña.`,
      confirmText: 'Enviar correo',
      cancelText: 'Cancelar',
      type: 'info',
      onConfirm: () => executeResetPassword(item)
    });
  };

  const executeDeleteUser = async (item) => {
    if (!item?.id) return;

    setError('');
    setSuccess('');
    setActionUserId(item.id);

    try {
      await adminFetch(
        `/api/admin/users/${item.id}`,
        {
          method: 'DELETE'
        }
      );

      const successMessage =
        `Usuario ${item.email} eliminado correctamente.`;

      setSuccess(successMessage);

      showSuccess(successMessage, {
        title: 'Usuario eliminado'
      });

      await loadUsers();
    } catch (err) {
      const errorMessage =
        err?.message ||
        'No fue posible eliminar el usuario.';

      setError(errorMessage);

      showError(errorMessage, {
        title: 'Error al eliminar usuario'
      });
    } finally {
      setActionUserId(null);
    }
  };

  const handleDeleteUser = (item) => {
    if (!item?.id) return;

    if (item.role === 'admin') {
      const message =
        'No se puede eliminar una cuenta administradora desde esta pantalla.';

      setError(message);

      showWarning(message, {
        title: 'Cuenta protegida'
      });

      return;
    }

    showConfirmation({
      title: 'Eliminar usuario',
      message:
        `Vas a eliminar definitivamente la cuenta ${item.email}. Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar definitivamente',
      cancelText: 'Cancelar',
      type: 'warning',
      onConfirm: () => executeDeleteUser(item)
    });
  };

  const handleTogglePriceUpdaterPermission = async (item) => {
    if (!item?.id || item?.role === 'admin') return;

    setError('');
    setSuccess('');
    setActionUserId(item.id);

    const enabled = !item.price_updater;

    try {
      await adminFetch(
        `/api/admin/users/${item.id}/permissions/price-updater`,
        {
          method: 'POST',
          body: JSON.stringify({
            enabled
          })
        }
      );

      const message = enabled
        ? `Acceso al Actualizador de precios concedido a ${item.email}.`
        : `Acceso al Actualizador de precios retirado a ${item.email}.`;

      setSuccess(message);

      showSuccess(message, {
        title: enabled
          ? 'Permiso concedido'
          : 'Permiso retirado'
      });

      await loadUsers();
    } catch (err) {
      const message =
        err?.message ||
        'No fue posible actualizar el permiso.';

      setError(message);

      showError(message, {
        title: 'Error de permisos'
      });
    } finally {
      setActionUserId(null);
    }
  };


  const formatDate = (value) => {
    if (!value) return '—';

    try {
      return new Intl.DateTimeFormat(
        'es-CO',
        {
          dateStyle: 'medium',
          timeStyle: 'short'
        }
      ).format(new Date(value));
    } catch {
      return '—';
    }
  };

  return (
    <div className="min-h-screen bg-background px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-6">

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-primary">
              <Icon name="ShieldCheck" size={18} />
              Administración
            </div>

            <h1 className="text-3xl font-bold text-text-primary">
              Usuarios
            </h1>

            <p className="mt-1 text-sm text-text-secondary">
              Administra quién puede acceder a Mercado Bulk.
            </p>
          </div>

          <button
            type="button"
            onClick={() => {
              setShowForm(prev => !prev);
              setError('');
              setSuccess('');
            }}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90"
          >
            <Icon
              name={showForm ? 'X' : 'UserPlus'}
              size={18}
            />

            {showForm
              ? 'Cancelar'
              : 'Agregar usuario'}
          </button>
        </div>

        {error && (
          <div className="flex items-start gap-3 rounded-xl border border-error/20 bg-error/10 p-4 text-sm text-error">
            <Icon name="AlertCircle" size={20} />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="flex items-start gap-3 rounded-xl border border-success/20 bg-success/10 p-4 text-sm text-success">
            <Icon name="CheckCircle" size={20} />
            <span>{success}</span>
          </div>
        )}

        {showForm && (
          <form
            onSubmit={handleInvite}
            className="rounded-2xl border border-border bg-surface p-6 shadow-sm"
          >
            <div className="mb-5">
              <h2 className="text-lg font-semibold text-text-primary">
                Invitar nuevo usuario
              </h2>

              <p className="mt-1 text-sm text-text-secondary">
                La persona recibirá un correo para activar su cuenta y crear su propia contraseña.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-text-primary">
                  Nombre completo *
                </label>

                <input
                  type="text"
                  value={form.full_name}
                  onChange={e =>
                    updateField(
                      'full_name',
                      e.target.value
                    )
                  }
                  className="w-full rounded-xl border border-border bg-background px-4 py-3 text-text-primary outline-none focus:ring-2 focus:ring-primary/40"
                  placeholder="Nombre de la persona"
                  required
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-text-primary">
                  Correo electrónico *
                </label>

                <input
                  type="email"
                  value={form.email}
                  onChange={e =>
                    updateField(
                      'email',
                      e.target.value
                    )
                  }
                  className="w-full rounded-xl border border-border bg-background px-4 py-3 text-text-primary outline-none focus:ring-2 focus:ring-primary/40"
                  placeholder="correo@ejemplo.com"
                  required
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-text-primary">
                  Empresa
                </label>

                <input
                  type="text"
                  value={form.business_name}
                  onChange={e =>
                    updateField(
                      'business_name',
                      e.target.value
                    )
                  }
                  className="w-full rounded-xl border border-border bg-background px-4 py-3 text-text-primary outline-none focus:ring-2 focus:ring-primary/40"
                  placeholder="Nombre de la empresa"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-text-primary">
                  Teléfono
                </label>

                <input
                  type="tel"
                  value={form.phone}
                  onChange={e =>
                    updateField(
                      'phone',
                      e.target.value
                    )
                  }
                  className="w-full rounded-xl border border-border bg-background px-4 py-3 text-text-primary outline-none focus:ring-2 focus:ring-primary/40"
                  placeholder="+57 300 000 0000"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-text-primary">
                  Mercado
                </label>

                <select
                  value={form.market}
                  onChange={e =>
                    updateField(
                      'market',
                      e.target.value
                    )
                  }
                  className="w-full rounded-xl border border-border bg-background px-4 py-3 text-text-primary outline-none focus:ring-2 focus:ring-primary/40"
                >
                  <option value="MCO">
                    Colombia (MCO)
                  </option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="submit"
                disabled={inviting}
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Icon
                  name={inviting ? 'Loader2' : 'Send'}
                  size={18}
                  className={
                    inviting
                      ? 'animate-spin'
                      : ''
                  }
                />

                {inviting
                  ? 'Enviando invitación...'
                  : 'Enviar invitación'}
              </button>
            </div>
          </form>
        )}

        <div className="rounded-2xl border border-border bg-surface p-6 shadow-sm">
          <div className="mb-5">
            <div className="flex items-center gap-2">
              <Icon
                name="BadgeDollarSign"
                size={20}
                className="text-primary"
              />

              <h2 className="text-lg font-semibold text-text-primary">
                Permisos del Actualizador de precios
              </h2>
            </div>

            <p className="mt-1 text-sm text-text-secondary">
              Los administradores tienen acceso automático. A los demás
              usuarios puedes concederles únicamente este módulo sin
              convertirlos en administradores.
            </p>
          </div>

          <div className="grid gap-3">
            {users.map(item => {
              const isAdmin =
                item?.role === 'admin';

              const hasAccess =
                isAdmin ||
                item?.price_updater === true;

              const isWorking =
                actionUserId === item.id;

              return (
                <div
                  key={`price-permission-${item.id}`}
                  className="flex flex-col gap-3 rounded-xl border border-border bg-background p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-text-primary">
                      {item.full_name || item.email}
                    </p>

                    <p className="truncate text-sm text-text-secondary">
                      {item.email}
                    </p>

                    <div className="mt-2 flex items-center gap-2">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                          hasAccess
                            ? 'bg-emerald-500/10 text-emerald-500'
                            : 'bg-muted text-text-secondary'
                        }`}
                      >
                        {hasAccess
                          ? 'Acceso a precios'
                          : 'Sin acceso a precios'}
                      </span>

                      {isAdmin && (
                        <span className="text-xs text-text-secondary">
                          Incluido por rol administrador
                        </span>
                      )}
                    </div>
                  </div>

                  {!isAdmin && (
                    <button
                      type="button"
                      disabled={isWorking}
                      onClick={() =>
                        handleTogglePriceUpdaterPermission(item)
                      }
                      className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
                        hasAccess
                          ? 'border border-error/30 text-error hover:bg-error/10'
                          : 'bg-primary text-white hover:bg-primary/90'
                      }`}
                    >
                      <Icon
                        name={
                          isWorking
                            ? 'Loader2'
                            : hasAccess
                              ? 'ShieldOff'
                              : 'ShieldCheck'
                        }
                        size={17}
                        className={
                          isWorking
                            ? 'animate-spin'
                            : ''
                        }
                      />

                      {isWorking
                        ? 'Guardando...'
                        : hasAccess
                          ? 'Quitar acceso'
                          : 'Dar acceso'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>


        <div className="overflow-hidden rounded-2xl border border-border bg-surface shadow-sm">
          <div className="border-b border-border px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-text-primary">
                  Usuarios registrados
                </h2>

                <p className="mt-0.5 text-sm text-text-secondary">
                  {users.length} cuenta{users.length === 1 ? '' : 's'}
                </p>
              </div>

              <button
                type="button"
                onClick={loadUsers}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-text-secondary transition hover:bg-muted"
              >
                <Icon
                  name="RefreshCw"
                  size={16}
                  className={
                    loading
                      ? 'animate-spin'
                      : ''
                  }
                />
                Actualizar
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-3 px-6 py-16 text-text-secondary">
              <Icon
                name="Loader2"
                size={22}
                className="animate-spin"
              />
              Cargando usuarios...
            </div>
          ) : users.length === 0 ? (
            <div className="px-6 py-16 text-center text-text-secondary">
              No hay usuarios registrados.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted/50 text-xs uppercase text-text-secondary">
                  <tr>
                    <th className="px-6 py-3">
                      Usuario
                    </th>
                    <th className="px-6 py-3">
                      Rol
                    </th>
                    <th className="px-6 py-3">
                      Estado
                    </th>
                    <th className="px-6 py-3">
                      Correo
                    </th>
                    <th className="px-6 py-3">
                      Último acceso
                    </th>

                    <th className="px-6 py-3 text-right">
                      Acciones
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-border">
                  {users.map(item => (
                    <tr
                      key={item.id}
                      className="hover:bg-muted/30"
                    >
                      <td className="px-6 py-4">
                        <div className="font-medium text-text-primary">
                          {item.full_name || 'Sin nombre'}
                        </div>

                        <div className="mt-0.5 text-xs text-text-secondary">
                          {item.email}
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <span
                          className={
                            item.role === 'admin'
                              ? 'rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary'
                              : 'rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-text-secondary'
                          }
                        >
                          {item.role === 'admin'
                            ? 'Administrador'
                            : 'Usuario'}
                        </span>
                      </td>

                      <td className="px-6 py-4">
                        <span
                          className={
                            item.active
                              ? 'inline-flex items-center gap-1.5 text-success'
                              : 'inline-flex items-center gap-1.5 text-error'
                          }
                        >
                          <span
                            className={
                              item.active
                                ? 'h-2 w-2 rounded-full bg-success'
                                : 'h-2 w-2 rounded-full bg-error'
                            }
                          />

                          {item.active
                            ? 'Activo'
                            : 'Desactivado'}
                        </span>
                      </td>

                      <td className="px-6 py-4">
                        {item.email_confirmed
                          ? (
                            <span className="text-success">
                              Confirmado
                            </span>
                          )
                          : (
                            <span className="text-warning">
                              Pendiente
                            </span>
                          )}
                      </td>

                      <td className="whitespace-nowrap px-6 py-4 text-text-secondary">
                        {formatDate(
                          item.last_sign_in_at
                        )}
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex flex-wrap justify-end gap-2">

                          {item.role !== 'admin' && (
                            <button
                              type="button"
                              disabled={actionUserId === item.id}
                              onClick={() => handleToggleUser(item)}
                              className="rounded-lg border border-border px-3 py-2 text-xs font-medium text-text-primary transition hover:bg-muted disabled:opacity-50"
                            >
                              {item.active
                                ? 'Desactivar'
                                : 'Reactivar'}
                            </button>
                          )}

                          <button
                            type="button"
                            disabled={actionUserId === item.id}
                            onClick={() => handleResetPassword(item)}
                            className="rounded-lg border border-border px-3 py-2 text-xs font-medium text-primary transition hover:bg-primary/10 disabled:opacity-50"
                          >
                            Contraseña
                          </button>

                          {item.role !== 'admin' && (
                            <button
                              type="button"
                              disabled={actionUserId === item.id}
                              onClick={() => handleDeleteUser(item)}
                              className="rounded-lg border border-error/30 px-3 py-2 text-xs font-medium text-error transition hover:bg-error/10 disabled:opacity-50"
                            >
                              Eliminar
                            </button>
                          )}

                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
